import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Clock, CheckCircle2, XCircle, Home, Star } from 'lucide-react'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'

const PAYMENT_AMOUNT = 9

const STEPS = [
  {
    key: 'animal', question: 'ฝันเห็นอะไร?', emoji: '👁',
    options: [
      { value: 'snake', label: '🐍 งู' }, { value: 'fish', label: '🐟 ปลา' },
      { value: 'elephant', label: '🐘 ช้าง' }, { value: 'tiger', label: '🐯 เสือ' },
      { value: 'bird', label: '🐦 นก' }, { value: 'dog', label: '🐕 สุนัข' },
      { value: 'cat', label: '🐈 แมว' }, { value: 'human', label: '👤 คน' },
      { value: 'spirit', label: '👻 วิญญาณ' }, { value: 'object', label: '📦 สิ่งของ' },
      { value: 'place', label: '🏛 สถานที่' }, { value: 'other', label: '✨ อื่นๆ' },
    ],
  },
  {
    key: 'feeling', question: 'รู้สึกอย่างไรในฝัน?', emoji: '💭',
    options: [
      { value: 'fear', label: '😨 กลัว' }, { value: 'happy', label: '😄 ดีใจ' },
      { value: 'confused', label: '😵 สับสน' }, { value: 'shocked', label: '😱 ตกใจ' },
      { value: 'neutral', label: '😐 เฉยๆ' }, { value: 'angry', label: '😡 โกรธ' },
      { value: 'sad', label: '😢 เศร้า' },
    ],
  },
  {
    key: 'context', question: 'เกิดขึ้นในบริบทไหน?', emoji: '🌙',
    options: [
      { value: 'home', label: '🏠 ที่บ้าน' }, { value: 'forest', label: '🌳 ป่า' },
      { value: 'water', label: '🌊 น้ำ/ทะเล' }, { value: 'temple', label: '⛩ วัด' },
      { value: 'still', label: '🧘 อยู่นิ่งๆ' }, { value: 'chasing', label: '🏃 วิ่งไล่' },
      { value: 'fighting', label: '⚔️ ต่อสู้' }, { value: 'falling', label: '⬇️ ตกลงมา' },
      { value: 'lost', label: '🔍 หลงทาง' }, { value: 'flying', label: '🕊 บิน' },
    ],
  },
]

interface DreamResult {
  interpretation: string
  lucky_numbers: number[]
}

interface PendingPayment {
  ref_code: string
  status: 'awaiting_slip' | 'pending_review' | 'completed' | 'rejected' | 'expired'
}

const PENDING_KEY = 'amulet_pending_payment'

function loadPending(): PendingPayment | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function savePending(p: PendingPayment | null) {
  if (p) localStorage.setItem(PENDING_KEY, JSON.stringify(p))
  else localStorage.removeItem(PENDING_KEY)
}

export default function DreamPage() {
  const navigate = useNavigate()
  const { lineUid, profile, setProfile } = useAppStore()
  const [step, setStep] = useState(0)
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<DreamResult | null>(null)
  const [errorType, setErrorType] = useState<'limit' | 'general' | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingPayment | null>(loadPending())
  const [popup, setPopup] = useState<{ type: 'success' | 'reject'; msg: string } | null>(null)
  const [confirmChoices, setConfirmChoices] = useState<Record<string, string> | null>(null)
  const [savedNums, setSavedNums] = useState<Set<string>>(new Set())
  const [savingNum, setSavingNum] = useState<string | null>(null)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)

  const currentStep = STEPS[step]

  // Refresh profile (re-fetch quota / paid_tickets)
  async function refreshProfile() {
    if (!lineUid) return
    try {
      const fresh = await api.user.profile(lineUid) as Parameters<typeof setProfile>[0]
      setProfile(fresh)
    } catch {}
  }

  // Poll pending payment status every 5s
  useEffect(() => {
    if (!pending) return
    if (pending.status !== 'pending_review' && pending.status !== 'awaiting_slip') return

    const id = setInterval(async () => {
      try {
        const res = await api.payment.status(pending.ref_code) as { status: string }
        if (res.status === 'completed') {
          setPopup({ type: 'success', msg: 'ชำระเงินเรียบร้อย! กดเริ่มทำนายฝันได้เลย 🎉' })
          savePending(null)
          setPending(null)
          setErrorType(null)
          await refreshProfile()
        } else if (res.status === 'rejected') {
          setPopup({ type: 'reject', msg: 'สลิปจ่ายเงินไม่ถูกต้อง กรุณาติดต่อ Admin' })
          savePending(null)
          setPending(null)
        } else if (res.status === 'expired') {
          savePending(null)
          setPending(null)
        } else if (res.status !== pending.status) {
          const updated = { ...pending, status: res.status as PendingPayment['status'] }
          savePending(updated)
          setPending(updated)
        }
      } catch {}
    }, 5000)

    return () => clearInterval(id)
  }, [pending, lineUid])

  function selectOption(value: string) {
    const newChoices = { ...choices, [currentStep.key]: value }
    setChoices(newChoices)
    if (step < STEPS.length - 1) {
      setStep(s => s + 1)
    } else {
      const willUseTicket = profile?.dream_quota?.used_today ?? false
      if (willUseTicket) {
        setConfirmChoices(newChoices)
      } else {
        submitDream(newChoices)
      }
    }
  }

  async function submitDream(finalChoices: Record<string, string>) {
    if (!lineUid) return
    setLoading(true)
    setErrorType(null)
    try {
      const res = await api.dream.interpret(
        lineUid, finalChoices.animal, finalChoices.feeling, finalChoices.context,
      ) as DreamResult
      setResult(res)
      // Refresh quota after successful dream (used 1 ticket / 1 free)
      await refreshProfile()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      if (msg.includes('daily_limit') || msg.includes('limit')) setErrorType('limit')
      else { setErrorType('general'); setErrorMsg(msg) }
    } finally {
      setLoading(false)
    }
  }

  // Fetch saved numbers when result appears
  useEffect(() => {
    if (!result || !lineUid) return
    api.number.list(lineUid)
      .then(list => setSavedNums(new Set(list.map(n => n.number))))
      .catch(() => {})
  }, [result, lineUid])

  async function toggleSave(n: number) {
    if (!lineUid || savingNum !== null) return
    const numStr = n.toString().padStart(2, '0')
    setSavingNum(numStr)
    setSaveMsg(null)
    try {
      if (savedNums.has(numStr)) {
        await api.number.unsave(lineUid, numStr)
        setSavedNums(prev => { const s = new Set(prev); s.delete(numStr); return s })
      } else {
        await api.number.save(lineUid, numStr)
        setSavedNums(prev => new Set(prev).add(numStr))
        setSaveMsg('บันทึกเลข ' + numStr + ' แล้ว ⭐')
        setTimeout(() => setSaveMsg(null), 2000)
      }
    } catch (err) {
      const code = (err as Error & { code?: string }).code
      if (code === 'limit_reached') {
        setSaveMsg('เต็มแล้ว — บันทึกได้สูงสุด 3 เลข กรุณาลบเก่าออกก่อน')
        setTimeout(() => setSaveMsg(null), 3000)
      }
    }
    finally { setSavingNum(null) }
  }

  function reset() {
    setStep(0); setChoices({}); setResult(null); setErrorType(null); setErrorMsg(null)
    setSavedNums(new Set()); setConfirmChoices(null)
  }

  function goPay() {
    navigate(`/payment?amount=${PAYMENT_AMOUNT}&guru_id=dream&draw_id=current`)
  }

  function finishAndGoHome() {
    setResult(null)
    navigate('/', { replace: true })
  }

  // Pending badge — แสดงเฉพาะหน้า limit เท่านั้น
  const PendingBadge = pending && pending.status === 'pending_review' ? (
    <div className="mx-4 mb-3 px-3 py-2 rounded-xl flex items-center gap-2 text-sm"
         style={{ background: 'var(--color-surface)', color: 'var(--color-gold)', border: '1px solid var(--color-gold)' }}>
      <Clock size={16} />
      <span className="flex-1">รอตรวจสอบการชำระเงิน</span>
      <span className="text-xs font-mono opacity-70">{pending.ref_code}</span>
    </div>
  ) : null

  // Popup
  const Popup = popup && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         onClick={() => setPopup(null)}>
      <div className="w-full max-w-sm rounded-3xl p-6 text-center"
           style={{ background: 'var(--color-surface)' }}
           onClick={e => e.stopPropagation()}>
        {popup.type === 'success' ? (
          <CheckCircle2 size={56} className="mx-auto mb-3" style={{ color: 'var(--color-success)' }} />
        ) : (
          <XCircle size={56} className="mx-auto mb-3" style={{ color: 'var(--color-danger)' }} />
        )}
        <p className="text-base font-semibold mb-5" style={{ color: 'var(--color-text)' }}>
          {popup.msg}
        </p>
        <button onClick={() => setPopup(null)}
                className="w-full py-3 rounded-xl font-semibold"
                style={{ background: popup.type === 'success' ? 'var(--color-success)' : 'var(--color-primary)', color: '#fff' }}>
          ตกลง
        </button>
      </div>
    </div>
  )

  // ── Result Screen — full-height, ไม่บัง bottom nav ─────
  if (result) {
    return (
      <div className="flex flex-col min-h-dvh p-4 pb-24" style={{ background: 'var(--color-bg)' }}>
        {Popup}
        <div className="flex flex-col items-center gap-6 py-8 flex-1 pb-6">
          <div className="text-5xl">✨</div>
          <h2 className="text-xl font-bold text-center" style={{ color: 'var(--color-gold)' }}>คำทำนาย</h2>
          <div className="w-full p-5 rounded-2xl text-center leading-relaxed card-amulet"
               style={{ color: 'var(--color-text)' }}>
            {result.interpretation}
          </div>
          <div className="w-full">
            <p className="text-center text-sm mb-1" style={{ color: 'var(--color-text-muted)' }}>🍀 เลขมงคลของคุณ</p>
            <p className="text-center text-xs mb-3" style={{ color: 'var(--color-text-soft)' }}>
              กด ⭐ เพื่อบันทึกเลข ({savedNums.size}/3)
            </p>
            <div className="flex justify-center gap-4">
              {result.lucky_numbers.map((n, i) => {
                const numStr  = n.toString().padStart(2, '0')
                const isSaved = savedNums.has(numStr)
                const isFull  = savedNums.size >= 3 && !isSaved
                const isSpinning = savingNum === numStr
                return (
                  <div key={i} className="relative">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold"
                         style={{ background: 'var(--gradient-gold)', color: '#fff', boxShadow: '0 4px 12px rgba(184, 148, 90, 0.3)' }}>
                      {numStr}
                    </div>
                    <button
                      onClick={() => !isFull && toggleSave(n)}
                      disabled={isSpinning || isFull}
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center transition-transform active:scale-90"
                      style={{
                        background:  isSaved ? 'var(--color-gold)' : isFull ? 'var(--color-surface-3)' : 'var(--color-surface)',
                        border: '1.5px solid ' + (isSaved ? 'var(--color-gold)' : 'var(--color-surface-3)'),
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        opacity: isFull ? 0.4 : 1,
                      }}>
                      <Star
                        size={13}
                        fill={isSaved ? '#fff' : 'none'}
                        color={isSaved ? '#fff' : 'var(--color-text-soft)'}
                      />
                    </button>
                  </div>
                )
              })}
            </div>
            {saveMsg && (
              <p className="text-center text-xs mt-3 font-medium"
                 style={{ color: saveMsg.startsWith('เต็ม') ? 'var(--color-danger)' : 'var(--color-gold-dark)' }}>
                {saveMsg}
              </p>
            )}
          </div>
          <p className="text-xs text-center px-8 pb-2" style={{ color: 'var(--color-text-muted)' }}>
            เพื่อความบันเทิงเท่านั้น ไม่ใช่คำแนะนำทางการเงิน
          </p>
          <button onClick={finishAndGoHome}
                  className="w-full mt-2 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 btn-gold shimmer">
            <Home size={18} /> เสร็จสิ้น กลับหน้าหลัก
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh gap-4"
           style={{ background: 'var(--color-bg)' }}>
        <div className="text-5xl animate-pulse">🔮</div>
        <p style={{ color: 'var(--color-text-muted)' }}>กำลังวิเคราะห์ฝันของคุณ...</p>
      </div>
    )
  }

  // ── Daily Limit ──────────────────────────────────────
  if (errorType === 'limit') {
    const tickets = profile?.dream_quota?.paid_tickets ?? 0

    return (
      <div className="flex flex-col min-h-dvh p-6 pb-24" style={{ background: 'var(--color-bg)' }}>
        {Popup}
        {PendingBadge}
        <div className="flex flex-col items-center justify-center flex-1 gap-5 text-center">
          <div className="text-5xl">🌙</div>
          <h2 className="text-xl font-bold" style={{ color: 'var(--color-text)' }}>
            ใช้สิทธิ์ฟรีวันนี้ครบแล้ว
          </h2>
          {tickets > 0 ? (
            <>
              <p className="text-sm px-4" style={{ color: 'var(--color-text-muted)' }}>
                คุณมีตั๋วอยู่ <span style={{ color: 'var(--color-gold)', fontWeight: 700 }}>{tickets}</span> ครั้ง<br/>
                กดเริ่มทำนายฝันได้เลย
              </p>
              <button onClick={reset}
                      className="w-full max-w-xs py-4 rounded-2xl font-bold text-lg btn-gold shimmer">
                🔮 เริ่มทำนายฝัน
              </button>
            </>
          ) : (
            <>
              <p className="text-sm px-4" style={{ color: 'var(--color-text-muted)' }}>
                ทำนายฝันเพิ่มเติมได้ทันที<br/>
                เพียง <span style={{ color: 'var(--color-gold)', fontWeight: 700 }}>{PAYMENT_AMOUNT} บาท</span> ต่อครั้ง
              </p>
              {pending && pending.status === 'pending_review' && (
                <p className="text-xs px-4" style={{ color: 'var(--color-gold)' }}>
                  ⏳ มีรายการรอตรวจสอบอยู่ — สามารถจ่ายเพิ่มได้ถ้าต้องการ
                </p>
              )}
              <div className="w-full max-w-xs flex flex-col gap-3 mt-2">
                <button onClick={goPay}
                        className="w-full py-4 rounded-2xl font-bold text-lg btn-gold shimmer">
                  💳 จ่ายเงิน {PAYMENT_AMOUNT}฿
                </button>
                <button onClick={() => navigate('/')}
                        className="w-full py-3 rounded-2xl font-semibold"
                        style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}>
                  กลับหน้าหลัก
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  if (errorType === 'general') {
    return (
      <div className="flex flex-col items-center justify-center min-h-dvh gap-4 p-6 text-center"
           style={{ background: 'var(--color-bg)' }}>
        {Popup}
        <div className="text-4xl">⚠️</div>
        <p style={{ color: 'var(--color-danger)' }}>{errorMsg}</p>
        <button onClick={reset} className="px-6 py-3 rounded-xl font-semibold"
                style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}>
          ลองใหม่
        </button>
      </div>
    )
  }

  // ── Question Steps — ไม่แสดง pending badge ────────────
  const tickets = profile?.dream_quota?.paid_tickets ?? 0

  const ConfirmDreamModal = confirmChoices && (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         onClick={() => setConfirmChoices(null)}>
      <div className="w-full max-w-sm rounded-3xl p-6 text-center"
           style={{ background: 'var(--color-surface)' }}
           onClick={e => e.stopPropagation()}>
        <div className="text-4xl mb-3">🔮</div>
        <h3 className="text-base font-bold mb-2" style={{ color: 'var(--color-text)' }}>
          ยืนยันการทำนายฝัน
        </h3>
        {tickets > 0 ? (
          <p className="text-sm mb-5" style={{ color: 'var(--color-text-muted)' }}>
            ใช้ <span style={{ color: 'var(--color-gold)', fontWeight: 700 }}>1 ตั๋ว</span> เพื่อทำนายฝันนี้
            <br /><span className="text-xs">คุณมี {tickets} ตั๋ว</span>
          </p>
        ) : (
          <p className="text-sm mb-5" style={{ color: '#e53e3e' }}>
            ไม่มีตั๋วเพียงพอ<br />กรุณาซื้อตั๋วก่อนทำนายฝัน
          </p>
        )}
        <div className="flex gap-3">
          <button onClick={() => setConfirmChoices(null)}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm"
                  style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            ยกเลิก
          </button>
          {tickets > 0 ? (
            <button
              onClick={() => { const c = confirmChoices; setConfirmChoices(null); submitDream(c) }}
              className="flex-1 py-3 rounded-xl font-semibold text-sm btn-gold shimmer">
              ยืนยัน
            </button>
          ) : (
            <button onClick={() => navigate('/store')}
                    className="flex-1 py-3 rounded-xl font-semibold text-sm btn-gold shimmer">
              ซื้อตั๋ว
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex flex-col min-h-dvh p-4" style={{ background: 'var(--color-bg)' }}>
      {Popup}
      {ConfirmDreamModal}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/')}
                style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>ทำนายฝัน</h1>
      </div>
      <div className="flex gap-2 mb-8">
        {STEPS.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full transition-colors"
               style={{ background: i <= step ? 'var(--color-primary)' : 'var(--color-surface)' }} />
        ))}
      </div>
      <div className="text-center mb-8">
        <div className="text-4xl mb-3">{currentStep.emoji}</div>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
          {currentStep.question}
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          ขั้นตอนที่ {step + 1} / {STEPS.length}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {currentStep.options.map(opt => (
          <button key={opt.value} onClick={() => selectOption(opt.value)}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl transition-transform active:scale-95 card-amulet">
            <span className="text-2xl">{opt.label.split(' ')[0]}</span>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {opt.label.split(' ').slice(1).join(' ')}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
